"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Mide el ancho real del gráfico y avisa cuando entra en pantalla.
 *
 * Por qué medir: el SVG se dibuja en píxeles reales (viewBox = ancho medido) para que
 * las etiquetas tengan siempre su tamaño de lectura y las líneas sigan finas, en 360 px
 * y en 1920 px. Sin JavaScript se usa `fallbackWidth` y el viewBox escala.
 *
 * `inView` se enciende una sola vez: la entrada es un gesto, no un efecto que se repite.
 */
export function useChartFrame<T extends HTMLElement>(fallbackWidth: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallbackWidth);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? 0);
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
  }, []);

  return { ref, width, inView };
}
